/**
 * Definition Diff Viewer (Spec 205 — US3 / FR-006)
 *
 * Shows the per-part unified diff of an item's definition between two recovery points.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 205-workspace-complete-dr
 */

import React from 'react';
import { Card, Badge, EmptyState, LoadingState, ErrorState } from '@/components/ui';
import { useDefinitionDiff } from '../../hooks/useWorkspaceRestore';
import { DefinitionDiffPart } from '../../api/workspaceRestore';

interface Props {
  workspaceId: string;
  fromRunId: string;
  toRunId: string;
  itemId: string;
}

const KIND_VARIANT: Record<DefinitionDiffPart['changeKind'], 'success' | 'warning' | 'danger' | 'info'> = {
  added: 'success',
  removed: 'danger',
  modified: 'warning',
  unchanged: 'info',
};

function DiffLines({ text }: { text: string }) {
  return (
    <pre className="overflow-auto rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 text-xs font-mono">
      {text.split('\n').map((line, i) => {
        const color = line.startsWith('+ ')
          ? 'text-[var(--color-status-success,green)]'
          : line.startsWith('- ')
            ? 'text-[var(--color-status-danger,red)]'
            : 'text-[var(--color-text-secondary)]';
        return (
          <div key={i} className={color}>
            {line || ' '}
          </div>
        );
      })}
    </pre>
  );
}

export const DefinitionDiffViewer: React.FC<Props> = ({ workspaceId, fromRunId, toRunId, itemId }) => {
  const { data, isLoading, isError, error } = useDefinitionDiff(workspaceId, fromRunId, toRunId, itemId);

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message={(error as Error)?.message ?? 'Failed to load diff'} />;
  if (!data) return <EmptyState title="No diff available" />;

  return (
    <Card>
      <div className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[var(--color-text-primary)]">{data.itemType}</span>
          <Badge variant={data.changed ? 'warning' : 'info'}>
            {data.changed ? 'Changed' : 'No changes'}
          </Badge>
        </div>
        {data.parts.map((p) => (
          <div key={p.path} className="space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <code className="text-[var(--color-text-secondary)]">{p.path}</code>
              <Badge variant={KIND_VARIANT[p.changeKind]}>{p.changeKind}</Badge>
            </div>
            {p.unifiedDiff && p.changeKind === 'modified' && <DiffLines text={p.unifiedDiff} />}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default DefinitionDiffViewer;
